import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  Row,
  RowData,
  SortingState,
  useReactTable,
} from "@tanstack/react-table"
import { useVirtualizer } from "@tanstack/react-virtual"
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query"
import { Pagination } from "@/utils/Pagination"
import {
  Center,
  Loader,
  Paper,
  PaperProps,
  Table,
  TableTdProps,
  TableThProps,
  TableTrProps,
} from "@mantine/core"
import { IconArrowDown, IconArrowUp } from "@tabler/icons-react"

export type TheasoftColumnDef<T extends RowData> = ColumnDef<T> & {
  headerProps?: TableThProps
  cellProps?: TableTdProps
}

export type RowProps = Omit<
  TableTrProps,
  "ref" | "key" | "data-index" | "display" | "pos"
>

interface Props<TData> {
  infiniteQueryResult: UseInfiniteQueryResult<InfiniteData<Pagination<TData>>>
  columnDefinitions: TheasoftColumnDef<TData>[]
  getRowProps?: (row: Row<TData>) => RowProps | undefined
  sorting?: SortingState
  onSortingChange?: (sorting: SortingState) => void
  tableContainerProps?: PaperProps
}

export function InfiniteScrollingTable<TData>({
  infiniteQueryResult,
  columnDefinitions,
  getRowProps,
  sorting,
  onSortingChange,
  tableContainerProps,
}: Props<TData>) {
  const tableContainerRef = React.useRef<HTMLDivElement>(null)

  // const [sorting, setSorting] = React.useState<SortingState>([])

  const columns = React.useMemo<TheasoftColumnDef<TData>[]>(
    () => columnDefinitions,
    [columnDefinitions]
  )

  const { data, fetchNextPage, isFetching, isLoading } = infiniteQueryResult

  //flatten the array of arrays from the useInfiniteQuery hook
  const flatData = React.useMemo(
    () => data?.pages?.flatMap((page) => page.data) ?? [],
    [data]
  )
  const totalDBRowCount = data?.pages?.[0]?.totalCount ?? 0
  const totalFetched = flatData.length

  //called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement
        //once the user has scrolled within 500px of the bottom of the table, fetch more data if we can
        if (
          scrollHeight - scrollTop - clientHeight < 500 &&
          !isFetching &&
          totalFetched < totalDBRowCount
        ) {
          fetchNextPage()
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount]
  )

  //a check on mount and after a fetch to see if the table is already scrolled to the bottom and immediately needs to fetch more data
  React.useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current)
  }, [fetchMoreOnBottomReached])

  const table = useReactTable({
    data: flatData,
    columns,
    state: {
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    debugTable: import.meta.env.DEV,
  })

  //scroll to top of table when sorting changes
  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    if (onSortingChange) {
      const newSorting =
        typeof updater === "function"
          ? updater(table.getState().sorting)
          : updater
      onSortingChange(newSorting)
      if (!!table.getRowModel().rows.length) {
        rowVirtualizer.scrollToIndex?.(0)
      }
    }
  }

  //since this table option is derived from table row model state, we're using the table.setOptions utility
  table.setOptions((prev) => ({
    ...prev,
    onSortingChange: handleSortingChange,
  }))

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 36.69, //estimate row height for accurate scrollbar dragging
    getScrollElement: () => tableContainerRef.current,
    //measure dynamic row height, except in firefox because it measures table border height incorrectly
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  })

  // if (isLoading) {
  //   return <Loader h={"100%"} />
  // }

  return (
    <Paper
      withBorder
      shadow="xs"
      onScroll={(e) => fetchMoreOnBottomReached(e.currentTarget)}
      ref={tableContainerRef}
      pos={"relative"}
      style={{
        overflow: "auto", //our scrollable table container
      }}
      {...tableContainerProps}
    >
      {/* Even though we're still using sematic table tags, we must use CSS grid and flexbox for dynamic row heights */}
      <Table stickyHeader display={"grid"}>
        <Table.Thead bg={"var(mantine"} display={"grid"}>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Tr
              key={headerGroup.id}
              style={{ display: "flex", width: "100%" }}
            >
              {headerGroup.headers.map((header) => {
                return (
                  <Table.Th
                    {...(header.column.columnDef as TheasoftColumnDef<TData>)
                      .headerProps} //allow passing in custom header props such as styles for specific columns
                    key={header.id}
                    style={{
                      display: "flex",
                      width: header.getSize(),
                      minWidth: 0,
                      overflow: "hidden",
                      ...((header.column.columnDef as TheasoftColumnDef<TData>)
                        .headerProps?.style ?? {}),
                    }}
                  >
                    <div
                      {...(onSortingChange && {
                        onClick: header.column.getToggleSortingHandler(),
                        style: {
                          cursor: header.column.getCanSort()
                            ? "pointer"
                            : "default",
                        },
                      })}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: <IconArrowUp size={12} />,
                        desc: <IconArrowDown size={12} />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  </Table.Th>
                )
              })}
            </Table.Tr>
          ))}
        </Table.Thead>
        {isLoading ? (
          <Table.Tbody display={"grid"}>
            <Table.Tr display={"flex"}>
              <Table.Td>
                <Center p={"md"} h={"100%"} w={"100%"}>
                  <Loader />
                </Center>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        ) : (
          <Table.Tbody
            display={"grid"}
            h={rowVirtualizer.getTotalSize()} //tells scrollbar how big the table is
            pos={"relative"} //needed for absolute positioning of rows
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index] as Row<TData>
              const rowProps = getRowProps?.(row)
              return (
                <Table.Tr
                  data-index={virtualRow.index} //needed for dynamic row height measurement
                  ref={(node) => rowVirtualizer.measureElement(node)} //measure dynamic row height
                  key={row.id}
                  display={"flex"}
                  pos={"absolute"}
                  w={"100%"}
                  {...rowProps} //allow passing in custom row props such as onClick handlers or styles for specific rows
                  style={{
                    cursor: rowProps?.onClick ? "pointer" : "default",
                    transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
                    ...rowProps?.style, //allow passing in custom styles for specific rows
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <Table.Td
                        {...(cell.column.columnDef as TheasoftColumnDef<TData>)
                          .cellProps} //allow passing in custom cell props such as styles for specific columns
                        key={cell.id}
                        w={cell.column.getSize()}
                        display={"flex"}
                        style={{
                          minWidth: 0,
                          overflow: "hidden",
                          ...((
                            cell.column.columnDef as TheasoftColumnDef<TData>
                          ).cellProps?.style ?? {}),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Td>
                    )
                  })}
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        )}
      </Table>
    </Paper>
  )
}
